import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import { useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
	Button,
	IconButton,
	Menu,
	MenuItem,
	Popover,
	SxProps,
	Theme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

interface ButtonProps {
	id: string;
	buttonClassName?: string;
	children?: any;
	type?: "submit" | "reset" | "button" | undefined;
	size?: "flex" | "large" | "medium" | "small" | "xsmall";
	disabled?: boolean;
	onClick: (e: any) => void;
}

function BlackButton(props: ButtonProps): JSX.Element {
	const size = props.size || "medium";
	return (
		<div className="button-wrapper black-button-wrapper">
			<button
				onClick={props.onClick}
				id={props.id}
				type={props.type}
				className={`common-button common-button-${size} common-black-button typography-body-02 ${
					props.buttonClassName || ""
				}`}
				disabled={Boolean(props.disabled)}
			>
				{props.children}
			</button>
		</div>
	);
}

function GreenButton(props: ButtonProps): JSX.Element {
	const size = props.size || "medium";

	return (
		<div className="button-wrapper green-button-wrapper">
			<button
				id={props.id}
				onClick={props.onClick}
				type={props.type}
				className={`common-button common-button-${size} common-green-button typography-body-02 ${
					props.buttonClassName || ""
				}`}
				disabled={Boolean(props.disabled)}
			>
				{props.children}
			</button>
		</div>
	);
}

function GreyButton(props: ButtonProps): JSX.Element {
	const size = props.size || "medium";

	return (
		<div className="button-wrapper grey-button-wrapper">
			<button
				id={props.id}
				onClick={props.onClick}
				type={props.type}
				className={`common-button common-button-${size} common-grey-button typography-body-02 ${
					props.buttonClassName || ""
				}`}
				disabled={Boolean(props.disabled)}
			>
				{props.children}
			</button>
		</div>
	);
}

function TextButton(props: ButtonProps): JSX.Element {
	const size = props.size || "medium";

	return (
		<div className="button-wrapper text-button-wrapper">
			<button
				id={props.id}
				onClick={props.onClick}
				type={props.type}
				className={`common-button common-button-${size} common-text-button typography-body-02 ${
					props.buttonClassName || ""
				}`}
				disabled={Boolean(props.disabled)}
			>
				{props.children}
			</button>
		</div>
	);
}

interface OutlinedButtonProps extends ButtonProps {
	outlineColor: string;
}

function OutlinedButton(props: OutlinedButtonProps): JSX.Element {
	const size = props.size || "medium";

	return (
		<div className="button-wrapper outlined-button-wrapper">
			<button
				id={props.id}
				onClick={props.onClick}
				type={props.type}
				className={`common-button common-button-${size} common-outlined-button typography-body-02 ${
					props.buttonClassName || ""
				}`}
				style={{ borderColor: props.outlineColor, color: props.outlineColor }}
				disabled={Boolean(props.disabled)}
			>
				{props.children}
			</button>
		</div>
	);
}

interface DropdownButtonProps extends ButtonProps {
	buttonColor?: "green" | "black" | "grey";
	className?: string;
	menuOptions: Array<{
		key: string;
		onClick: () => void;
		children: any;
		widthToShow?: number;
		hasSubmenu?: boolean; // Flag to determine if this item has a submenu
		submenuOptions?: Array<{
			key: string;
			children: React.ReactNode;
			onClick: () => void;
		}>; // Submenu items
	}>;
	container?: HTMLElement | null;
}

function DropdownButton(props: DropdownButtonProps): JSX.Element {
	const [anchorEl, setAnchorEl] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [containerWidth, setContainerWidth] = useState<number>(0);

	const rightIconClassName: string = "button-with-right-icon";

	const handleClick = (event: any) => {
		setAnchorEl(event.currentTarget);
		setIsOpen(!isOpen); // Toggle menu open/close
		if (props.container) {
			const width = props.container.offsetWidth;
			setContainerWidth(width);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
		setIsOpen(false); // Close menu
	};
	let button: React.ReactNode;
	switch (props.buttonColor) {
		case "green":
			button = (
				<GreenButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={Boolean(props.disabled)}
					buttonClassName={props.buttonClassName + ` ${rightIconClassName}`}
					onClick={(e) => {
						handleClick(e);
					}} // Open dropdown on button click
				>
					{props.children}
					{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
				</GreenButton>
			);
			break;
		case "black":
			button = (
				<BlackButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={Boolean(props.disabled)}
					buttonClassName={props.buttonClassName + ` ${rightIconClassName}`}
					onClick={(e) => {
						handleClick(e);
					}} // Open dropdown on button click
				>
					{props.children}
					{isOpen ? (
						<KeyboardArrowUpIcon sx={{ color: "white" }} />
					) : (
						<KeyboardArrowDownIcon sx={{ color: "white" }} />
					)}
				</BlackButton>
			);
			break;
		case "grey":
			button = (
				<GreyButton
					id={props.id}
					type={props.type}
					size={props.size}
					disabled={Boolean(props.disabled)}
					buttonClassName={props.buttonClassName + ` ${rightIconClassName}`}
					onClick={(e) => {
						handleClick(e);
					}} // Open dropdown on button click
				>
					{props.children}
					{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
				</GreyButton>
			);
			break;
	}

	return (
		<div className={props.className}>
			{button}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)} // Open menu if anchorEl is set
				onClose={handleClose} // Close menu when an item is clicked or clicked outside
			>
				{props.menuOptions.map((option) => {
					return (
						<MenuItem
							sx={
								option.widthToShow
									? {
											display:
												option.widthToShow > containerWidth ? "flex" : "none",
									  }
									: {}
							}
							key={option.key}
							onClick={() => {
								option.onClick();
								handleClose();
							}}
						>
							{option.children}
						</MenuItem>
					);
				})}
			</Menu>
		</div>
	);
}

function KebabDropdownButton(props: DropdownButtonProps): JSX.Element {
	const [anchorEl, setAnchorEl] = useState(null);
	const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(
		null
	); // For submenu

	const [isOpen, setIsOpen] = useState(false);

	const handleClick = (event: any) => {
		event.stopPropagation();
		event.preventDefault();
		setAnchorEl(event.currentTarget);
		setIsOpen(!isOpen); // Toggle menu open/close
	};

	const handleClose = (event: any) => {
		event.stopPropagation();
		setAnchorEl(null);
		setSubmenuAnchorEl(null); // Close submenu
		setIsOpen(false); // Close menu
	};
	const handleSubmenuOpen = (event: any) => {
		setSubmenuAnchorEl(event.currentTarget); // Open submenu for specific item
	};

	const handleSubmenuClose = () => {
		setSubmenuAnchorEl(null); // Close submenu
	};
	return (
		<div>
			<IconButton
				id={props.id}
				disabled={Boolean(props.disabled)}
				onClick={handleClick}
			>
				<MoreVertIcon /> {/* Kebab (More) icon */}
			</IconButton>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)} // Open menu if anchorEl is set
				onClose={handleClose} // Close menu when an item is clicked or clicked outside
			>
				{props.menuOptions.map((option) => (
					<MenuItem
						key={option.key}
						onClick={(e) => {
							e.stopPropagation();
							option.onClick();
							if (!option.hasSubmenu) {
								handleClose(e);
							}
						}}
						onMouseEnter={option.hasSubmenu ? handleSubmenuOpen : undefined} // Open submenu on hover
						onMouseLeave={option.hasSubmenu ? handleSubmenuClose : undefined} // Open submenu on hover
					>
						{option.children}

						{/* Submenu */}
						{option.hasSubmenu && option.submenuOptions && (
							<Menu
								anchorEl={submenuAnchorEl} // Set submenu anchorEl
								open={Boolean(submenuAnchorEl)} // Open submenu if anchorEl is set
								onClose={handleSubmenuClose} // Close submenu
								anchorOrigin={{
									vertical: "top",
									horizontal: "right",
								}}
								transformOrigin={{
									vertical: "top",
									horizontal: 5,
								}}
								style={{ pointerEvents: "none" }}
								hideBackdrop
							>
								{option.submenuOptions.map((submenuOption) => (
									<MenuItem
										key={submenuOption.key}
										onClick={(e) => {
											e.stopPropagation();
											submenuOption.onClick();
											handleSubmenuClose();
											handleClose(e);
										}}
										style={{ pointerEvents: "auto" }}
									>
										{submenuOption.children}
									</MenuItem>
								))}
							</Menu>
						)}
					</MenuItem>
				))}
			</Menu>
		</div>
	);
}

interface CustomIconButtonProps {
	id: string;
	buttonClassName?: string;
	icon: any;
	type?: "submit" | "reset" | "button" | undefined;
	size?: "medium" | "small";
	disabled?: boolean;
	onClick: (e: any) => void;
}

function CustomIconButton(props: CustomIconButtonProps): JSX.Element {
	const size = props.size || "medium";
	let width = 44;
	let height = 44;
	let padding = "10px 20px";
	if (size == "small") {
		width = 32;
		height = 32;
		padding = "6px 8px";
	}

	return (
		<Button
			id={props.id}
			onClick={props.onClick}
			type={props.type}
			className={`icon-button icon-button-${size} ${
				props.buttonClassName || ""
			}`}
			sx={{
				minWidth: "auto", // Remove the default min-width
				width: width,
				height: height,
				padding: padding,
				".MuiSvgIcon-root": {
					color: NeutralColor.NEUTRAL_GREY_950, // Change icon color when the button is pressed
				},

				"&.Mui-disabled .MuiSvgIcon-root": {
					color: NeutralColor.NEUTRAL_GREY_300, // Change icon color when button is disabled
				},
				"&:focus": {
					color: "transparent", // Make sure focus color is transparent
					outline: "none", // Remove the focus outline (border)
					border: "none ", // Remove the default border on focus
				},

				"&:active": {
					outline: "none", // Make sure no border shows when the button is active (clicked)
				},
			}}
			disabled={Boolean(props.disabled)}
		>
			{props.icon}
		</Button>
	);
}
export {
	BlackButton,
	GreenButton,
	GreyButton,
	OutlinedButton,
	DropdownButton,
	KebabDropdownButton,
	TextButton,
	CustomIconButton,
};
